import { useState } from 'react';
import {
  Box, Button, Card, Chip, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, InputAdornment, Stack, TextField,
  Typography, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../../components/iconify';
import { OrderResponse } from '../../@types/order/order.type';
import { OrderedProductResponse } from '../../@types/orderedProduct/orderedProductResponse.type';
import { ProductResponse } from '../../@types/product/product.type';

interface VoiceAgentOrderCartProps {
  order: OrderResponse;
  products: ProductResponse[];
  onSendToPOS: () => void;
  onRelease: () => void;
  onAddProduct: (productId: string, quantity: number) => void;
  onUpdateQty: (orderedProductId: string, quantity: number) => void;
  onRemoveItem: (orderedProductId: string) => void;
  isLoading?: boolean;
  isAgentSpeaking?: boolean;
  isClientSpeaking?: boolean;
}

function fCurrency(value: number) {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
}

function computeTotal(order: OrderResponse) {
  const subtotal = (order.orderedProducts || []).reduce((acc, item) => {
    const base = item.unitPrice != null ? Number(item.unitPrice) : Number(item.product?.price || 0);
    const extras = (item.trioSelections || []).reduce((s: number, sel: any) => s + Number(sel.price || 0), 0);
    return acc + (base + extras) * Number(item.quantity || 0);
  }, 0);
  const fedTax = subtotal * ((order.federalTaxRate ?? 5) / 100);
  const provTax = subtotal * ((order.provincialTaxRate ?? 9.975) / 100);
  return { subtotal, tax: fedTax + provTax, total: subtotal + fedTax + provTax };
}

function getProductName(p: ProductResponse): string {
  const fr = p.translations?.find((t: any) => t.language === 'FR');
  const en = p.translations?.find((t: any) => t.language === 'EN');
  return fr?.name || en?.name || 'Produit';
}

export default function VoiceAgentOrderCart({
  order, products, onSendToPOS, onRelease,
  onAddProduct, onUpdateQty, onRemoveItem,
  isLoading, isAgentSpeaking, isClientSpeaking,
}: VoiceAgentOrderCartProps) {
  const theme = useTheme();
  const { subtotal, tax, total } = computeTotal(order);
  const items = order.orderedProducts || [];
  const [selectorOpen, setSelectorOpen] = useState(false);

  return (
    <>
      <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        {/* Header */}
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[500], 0.03) }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>Panier</Typography>
              <Chip label={`${items.length} article${items.length > 1 ? 's' : ''}`} size="small"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(theme.palette.grey[500], 0.1), color: 'text.secondary' }} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {isClientSpeaking && (
                <Chip size="small" icon={<Iconify icon="mdi:account-voice" width={13} />} label="Client parle"
                  sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.dark', '& .MuiChip-icon': { color: 'warning.dark' } }} />
              )}
              {isAgentSpeaking && (
                <Chip size="small" icon={<Iconify icon="mdi:microphone" width={13} />} label="Vous parlez"
                  sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', '& .MuiChip-icon': { color: 'success.dark' },
                    animation: 'commPulse 1.2s ease-in-out infinite',
                    '@keyframes commPulse': { '0%,100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.4)}` }, '50%': { boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0)}` } } }} />
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Items */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Iconify icon="mdi:cart-outline" width={36} sx={{ color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.disabled">Panier vide</Typography>
            </Box>
          ) : (
            <Stack spacing={0.75}>
              {items.map((item: OrderedProductResponse) => {
                const unitPrice = item.unitPrice != null ? Number(item.unitPrice) : Number(item.product?.price || 0);
                const extras = (item.trioSelections || []).reduce((s: number, sel: any) => s + Number(sel.price || 0), 0);
                const lineTotal = (unitPrice + extras) * Number(item.quantity || 0);

                return (
                  <Box key={item.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.04), border: `1px solid ${alpha(theme.palette.divider, 0.07)}` }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      {/* Qty controls */}
                      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
                        <IconButton size="small" onClick={() => onUpdateQty(item.id, item.quantity - 1)} disabled={isLoading}
                          sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.grey[500], 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}>
                          <Iconify icon="mdi:minus" width={12} />
                        </IconButton>
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.875rem' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton size="small" onClick={() => onUpdateQty(item.id, item.quantity + 1)} disabled={isLoading}
                          sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.grey[500], 0.1), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) } }}>
                          <Iconify icon="mdi:plus" width={12} />
                        </IconButton>
                      </Stack>

                      {/* Name + extras */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{item.product?.translations?.find(t => t.language === 'FR')?.name || item.product?.translations?.[0]?.name || 'Produit'}</Typography>
                        {(item.trioSelections || []).map((sel: any, i: number) => (
                          <Typography key={i} variant="caption" color="text.disabled" noWrap>+ {sel.productName || sel.name}</Typography>
                        ))}
                      </Box>

                      {/* Price + remove */}
                      <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                        <Typography variant="body2" fontWeight={700}>{fCurrency(lineTotal)}</Typography>
                        <IconButton size="small" onClick={() => onRemoveItem(item.id)} disabled={isLoading}
                          sx={{ width: 20, height: 20, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <Iconify icon="mdi:close" width={12} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          {/* Add product button */}
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<Iconify icon="mdi:plus" width={16} />}
            onClick={() => setSelectorOpen(true)}
            sx={{ mt: 1.5, borderRadius: 1.5, borderStyle: 'dashed', color: 'text.secondary', borderColor: alpha(theme.palette.divider, 0.3), '&:hover': { borderStyle: 'dashed', borderColor: 'primary.main', color: 'primary.main' } }}
          >
            Ajouter un produit
          </Button>
        </Box>

        {/* Totals + actions */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Sous-total</Typography>
              <Typography variant="caption" color="text.secondary">{fCurrency(subtotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Taxes</Typography>
              <Typography variant="caption" color="text.secondary">{fCurrency(tax)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
              <Typography variant="subtitle2" fontWeight={800} color="primary.main">{fCurrency(total)}</Typography>
            </Stack>
          </Stack>

          <Stack spacing={1}>
            <Button fullWidth variant="contained" color="primary" size="large"
              disabled={isLoading || items.length === 0} onClick={onSendToPOS}
              startIcon={<Iconify icon="mdi:cash-register" width={19} />}
              sx={{ borderRadius: 2, fontWeight: 700, py: 1.25, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` }}>
              Envoyer au POS
            </Button>
            <Button fullWidth variant="outlined" color="warning" size="medium"
              disabled={isLoading} onClick={onRelease}
              startIcon={<Iconify icon="mdi:robot" width={17} />}
              sx={{ borderRadius: 2, fontWeight: 600 }}>
              Rendre la main à l&apos;IA
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Product selector dialog */}
      <ProductSelectorDialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        products={products}
        onAdd={(productId, qty) => { onAddProduct(productId, qty); setSelectorOpen(false); }}
      />
    </>
  );
}

// ── Product selector dialog ─────────────────────────────────────────────────
function ProductSelectorDialog({ open, onClose, products, onAdd }: {
  open: boolean;
  onClose: () => void;
  products: ProductResponse[];
  onAdd: (productId: string, quantity: number) => void;
}) {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});

  const filtered = (products || []).filter((p) => {
    const name = getProductName(p).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const getQty = (id: string) => qty[id] ?? 1;
  const setProductQty = (id: string, v: number) => setQty(prev => ({ ...prev, [id]: Math.max(1, v) }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>Ajouter un produit</Typography>
          <IconButton size="small" onClick={onClose}><Iconify icon="mdi:close" width={18} /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          fullWidth size="small" placeholder="Rechercher…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Iconify icon="mdi:magnify" width={18} sx={{ color: 'text.disabled' }} /></InputAdornment>,
          }}
        />
        <Stack spacing={0.75} sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 4 }}>
              Aucun produit trouvé
            </Typography>
          )}
          {filtered.map((p) => (
            <Box key={p.id} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.grey[500], 0.03) }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{getProductName(p)}</Typography>
                  <Typography variant="caption" color="text.secondary">{fCurrency(p.price)}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton size="small" onClick={() => setProductQty(p.id, getQty(p.id) - 1)}
                    sx={{ width: 26, height: 26, bgcolor: alpha(theme.palette.grey[500], 0.1) }}>
                    <Iconify icon="mdi:minus" width={13} />
                  </IconButton>
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 22, textAlign: 'center' }}>
                    {getQty(p.id)}
                  </Typography>
                  <IconButton size="small" onClick={() => setProductQty(p.id, getQty(p.id) + 1)}
                    sx={{ width: 26, height: 26, bgcolor: alpha(theme.palette.grey[500], 0.1) }}>
                    <Iconify icon="mdi:plus" width={13} />
                  </IconButton>
                  <Button size="small" variant="contained" onClick={() => onAdd(p.id, getQty(p.id))}
                    sx={{ borderRadius: 1.5, minWidth: 70, fontWeight: 700, fontSize: '0.75rem' }}>
                    Ajouter
                  </Button>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
